import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Skeleton } from "../../components/ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Switch } from "../../components/ui/switch";
import {
  Sparkles, Settings as SettingsIcon, Building2, CalendarDays, CreditCard,
  TrendingUp, Save, Plus, Trash2, Loader2, Tags, IndianRupee, ShieldCheck,
  Target, Zap,
} from "lucide-react";
import { toast } from "sonner";
import { formatISTDayLabel } from "../../lib/datetime";
import { formatINR } from "../../lib/expenseOptions";
import StickyHeading from "../../components/common/StickyHeading";

export default function SettingsPage() {
  return (
    <div className="space-y-6" data-testid="settings-page">
      <StickyHeading
        icon={Sparkles}
        label="Settings"
        title="Company configuration"
        subtitle="Manage company profile, holidays, leave policy, expense categories and caps, UCPMP and visit frequency targets."
        testId="settings-heading"
      />

      <CompanyProfileSection />
      <LeavePolicySection />
      <EscalationThresholdSection />
      <ExpenseCapsSection />
      <UcpmpSalesBasisSection />
      <VisitFrequencySection />
      <PriceFieldSection />
      <HolidaysSection />
      <StringListSection
        title="Expense categories"
        description="Categories available to Sales Officers when raising an expense claim."
        icon={CreditCard}
        endpoint="/settings/expense-types"
        deleteParam={(name) => encodeURIComponent(name)}
        testIdPrefix="expense-types"
      />
      <StringListSection
        title="Ad hoc call reasons"
        description="Reasons a Sales Officer can give when logging a call that was not in the tour plan."
        icon={Zap}
        endpoint="/settings/deviation-reasons"
        deleteParam={(name) => encodeURIComponent(name)}
        testIdPrefix="deviation-reasons"
      />
    </div>
  );
}

function SectionShell({ title, description, icon: Icon, children, testId }) {
  return (
    <section
      className="bg-white border border-callisto rounded-xl p-5 md:p-6"
      data-testid={testId}
    >
      <div className="flex items-center gap-3 mb-5">
        <div className="h-10 w-10 rounded-lg bg-callisto-navy text-callisto-gold flex items-center justify-center">
          <Icon size={18} />
        </div>
        <div>
          <h2 className="text-base md:text-lg font-bold text-callisto-navy">{title}</h2>
          {description && <p className="text-[12px] text-callisto-muted">{description}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

function CompanyProfileSection() {
  const [doc, setDoc] = useState(null);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/company")
      .then(({ data }) => {
        setDoc(data);
        setName(data.name || "");
        setAddress(data.address || "");
        setLogoUrl(data.logo_url || "");
      })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, []);

  async function save() {
    if (!name.trim()) return toast.error("Company name is required");
    setSaving(true);
    try {
      const { data } = await api.put("/settings/company", { name: name.trim(), address, logo_url: logoUrl });
      setDoc(data);
      toast.success("Company profile saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <SectionShell title="Company profile" description="Displayed in headers, emails, and PDF exports." icon={Building2} testId="settings-company-section">
      {!doc ? (
        <Skeleton className="h-32 w-full" />
      ) : (
        <div className="space-y-4">
          <div>
            <Label className="text-callisto-navy">Company name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-11 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
              data-testid="settings-company-name"
            />
          </div>
          <div>
            <Label className="text-callisto-navy">Address</Label>
            <Input
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Headquarters address"
              className="h-11 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
              data-testid="settings-company-address"
            />
          </div>
          <div>
            <Label className="text-callisto-navy">Logo URL</Label>
            <Input
              value={logoUrl}
              onChange={(e) => setLogoUrl(e.target.value)}
              placeholder="https://…/logo.svg (optional)"
              className="h-11 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
              data-testid="settings-company-logo"
            />
          </div>
          <div className="flex justify-end">
            <Button
              onClick={save}
              disabled={saving}
              className="h-11 bg-callisto-navy hover:bg-callisto-navy/90 text-white"
              data-testid="settings-company-save"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save size={14} className="mr-1.5" /> Save profile</>)}
            </Button>
          </div>
        </div>
      )}
    </SectionShell>
  );
}

function LeavePolicySection() {
  const [doc, setDoc] = useState(null);
  const [vals, setVals] = useState({
    cl_annual: 12,
    pl_accrual_divisor: 20,
    pl_cap: 45,
    pl_warning: 40,
    pl_min_spell_days: 2,
    pl_accrual_epoch: "2026-09-01",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/leave-policy").then(({ data }) => {
      setDoc(data);
      setVals((v) => ({
        cl_annual: data.cl_annual ?? v.cl_annual,
        pl_accrual_divisor: data.pl_accrual_divisor ?? v.pl_accrual_divisor,
        pl_cap: data.pl_cap ?? v.pl_cap,
        pl_warning: data.pl_warning ?? v.pl_warning,
        pl_min_spell_days: data.pl_min_spell_days ?? v.pl_min_spell_days,
        pl_accrual_epoch: data.pl_accrual_epoch ?? v.pl_accrual_epoch,
      }));
    });
  }, []);

  async function save() {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/leave-policy", vals);
      setDoc(data);
      toast.success("Leave policy saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }

  // Two types (P5/P8): CL is an annual grant; every PL number parameterises
  // the COMPUTED accrual — there is no PL annual figure to type anywhere.
  const NUMBER_FIELDS = [
    { key: "cl_annual", label: "Casual (CL) — days per year", testId: "settings-leave-cl", max: 60 },
    { key: "pl_accrual_divisor", label: "PL — working days per accrued day", testId: "settings-leave-pl-divisor", max: 365 },
    { key: "pl_cap", label: "PL ceiling (days)", testId: "settings-leave-pl-cap", max: 365 },
    { key: "pl_warning", label: "PL warning threshold (days)", testId: "settings-leave-pl-warning", max: 365 },
    { key: "pl_min_spell_days", label: "PL minimum spell (working days, strictly longer)", testId: "settings-leave-pl-spell", max: 30 },
  ];

  return (
    <SectionShell
      title="Leave policy"
      description="Two types. CL is granted annually and lapses at year end; sickness is a reason under CL. PL accrues from working days on duty, carries forward to its ceiling, and days beyond the ceiling lapse visibly."
      icon={CalendarDays}
      testId="settings-leave-policy-section"
    >
      {!doc ? (
        <Skeleton className="h-32 w-full" />
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {NUMBER_FIELDS.map((f) => (
              <div key={f.key}>
                <Label className="text-callisto-navy">{f.label}</Label>
                <Input
                  type="number"
                  min={0}
                  max={f.max}
                  value={vals[f.key]}
                  onChange={(e) => setVals((v) => ({ ...v, [f.key]: parseFloat(e.target.value || "0") }))}
                  className="h-11 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                  data-testid={f.testId}
                />
              </div>
            ))}
            <div>
              <Label className="text-callisto-navy">PL accrual starts from</Label>
              <Input
                type="date"
                value={vals.pl_accrual_epoch}
                onChange={(e) => setVals((v) => ({ ...v, pl_accrual_epoch: e.target.value }))}
                className="h-11 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                data-testid="settings-leave-pl-epoch"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              onClick={save}
              disabled={saving}
              className="h-11 bg-callisto-navy hover:bg-callisto-navy/90 text-white"
              data-testid="settings-leave-save"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save size={14} className="mr-1.5" /> Save policy</>)}
            </Button>
          </div>
        </div>
      )}
    </SectionShell>
  );
}

// Mirrors the backend's own bound (`routes_settings.ApprovalPolicy`,
// `le=10_000_000`) so a value the API would reject is caught here instead of
// round-tripping to find out. `ExpenseCreate.amount` shares the same cap —
// two independent screens, matching backend limits, and this one guards
// against the frontend and backend disagreeing about the ceiling.
const MAX_THRESHOLD = 10_000_000;

// The same ceiling the backend carries (`routes_settings._MAX_MONTHLY_VISITS`,
// and `DoctorCreate.monthly_visit_target` before it): a monthly target above 31
// is not a target, it is a typo. Here for the same reason `MAX_THRESHOLD` is —
// so a value the API would reject is caught before the round trip — and it is a
// BOUND, not a list. A master list would have to be fetched (#78).
const MAX_MONTHLY_VISITS = 31;

function EscalationThresholdSection() {
  const [doc, setDoc] = useState(null);
  const [raw, setRaw] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/approval-policy")
      .then(({ data }) => {
        setDoc(data);
        setRaw(String(data.expense_escalation_threshold ?? 5000));
      })
      // `LeavePolicySection`'s GET has no `.catch()` — a failed load there
      // leaves the section on its skeleton forever, silently. Not repeated
      // here: a threshold nobody can see or edit is worse than a leave-day
      // count that is, so a failed load says so.
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, []);

  async function save() {
    // Validated here rather than left to the API, unlike `LeavePolicySection`
    // — a mistyped leave-day count is cosmetic; a mistyped threshold changes
    // who signs off on company money. `Number("")` is 0, not NaN, so an empty
    // field is caught by the range check rather than slipping through as 0
    // silently — 0 is a legitimate, deliberate value ("escalate everything")
    // and must be typed, not defaulted to by leaving the box blank.
    if (raw.trim() === "") return toast.error("Enter a threshold");
    const value = Number(raw);
    if (!Number.isFinite(value) || value < 0 || value > MAX_THRESHOLD) {
      return toast.error(`Enter an amount between ₹0 and ${formatINR(MAX_THRESHOLD)}`);
    }
    setSaving(true);
    try {
      const { data } = await api.put("/settings/approval-policy", {
        expense_escalation_threshold: value,
      });
      setDoc(data);
      setRaw(String(data.expense_escalation_threshold));
      toast.success("Escalation threshold saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <SectionShell
      title="Expense escalation"
      description="Above this amount, a Sales Officer's expense skips their Sales Manager and goes to the Admin Manager, then the Director instead."
      icon={TrendingUp}
      testId="settings-escalation-section"
    >
      {!doc ? (
        <Skeleton className="h-24 w-full" />
      ) : (
        <div className="space-y-3">
          <div className="max-w-xs">
            <Label className="text-callisto-navy">Escalation threshold (₹)</Label>
            <Input
              type="number"
              inputMode="decimal"
              min={0}
              max={MAX_THRESHOLD}
              step="0.01"
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              className="h-11 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
              data-testid="settings-escalation-threshold"
            />
            <p className="mt-1 text-xs text-callisto-muted">
              Strictly greater than this amount escalates — a claim of exactly{" "}
              {formatINR(doc.expense_escalation_threshold)} does not.
            </p>
          </div>
          <div className="flex justify-end">
            <Button
              onClick={save}
              disabled={saving}
              className="h-11 bg-callisto-navy hover:bg-callisto-navy/90 text-white"
              data-testid="settings-escalation-save"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save size={14} className="mr-1.5" /> Save threshold</>)}
            </Button>
          </div>
        </div>
      )}
    </SectionShell>
  );
}

// P2's two spare price columns. The backend has had `GET`/`PUT
// /settings/products/price-fields` since Block 3 and the Product Master has
// read the configuration since Block 3 — but nothing could WRITE it, so the
// answer "provision for 2 further price fields, admin/tech-admin enable-able"
// was half built: the consumer existed and the editor did not, which means the
// slots could never be enabled at all from inside the app.
//
// The slot keys are NOT listed here. They are whatever the endpoint returns, so
// a third slot would appear without a frontend change and a removed one would
// vanish — the same reason the Product Master no longer keeps its own copy of
// the price-field list.
//
// No local "a label is required" rule either, deliberately. The backend refuses
// an enabled slot with a blank label (`PriceFieldSettings._named_when_on`) and
// its message is the one shown. Two copies of one rule is how the two drift.
function PriceFieldSection() {
  const [slots, setSlots] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/products/price-fields")
      .then(({ data }) => setSlots(data))
      // A failed load must say so rather than sit on a skeleton — a section
      // that silently shows nothing reads as "there is nothing to configure".
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, []);

  function edit(key, patch) {
    setSlots((s) => ({ ...s, [key]: { ...s[key], ...patch } }));
  }

  async function save() {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/products/price-fields", slots);
      setSlots(data);
      toast.success("Price fields saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }

  const keys = slots ? Object.keys(slots) : [];

  return (
    <SectionShell
      title="Extra price fields"
      description="Two spare price columns on the product master. Switch one on and give it a name; it then appears on the medicine form and in the price history."
      icon={Tags}
      testId="settings-price-fields-section"
    >
      {error ? (
        <div
          className="rounded-lg border border-red-200 bg-red-50 text-red-800 px-3 py-2.5 text-sm"
          data-testid="settings-price-fields-error"
        >
          {error}
        </div>
      ) : !slots ? (
        <Skeleton className="h-24 w-full" />
      ) : (
        <div className="space-y-3">
          {keys.map((key) => (
            <div
              key={key}
              className="rounded-lg border border-callisto p-3 grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-3 items-center"
              data-testid={`settings-price-field-${key}`}
            >
              <div className="flex items-center gap-2">
                <Switch
                  checked={!!slots[key].enabled}
                  onCheckedChange={(v) => edit(key, { enabled: !!v })}
                  data-testid={`settings-price-field-${key}-enabled`}
                />
                <Label className="text-[12px] text-callisto-body">
                  {slots[key].enabled ? "On" : "Off"}
                </Label>
              </div>
              <Input
                value={slots[key].label || ""}
                onChange={(e) => edit(key, { label: e.target.value })}
                placeholder="What is this column called? e.g. Institutional rate"
                className="h-11 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                data-testid={`settings-price-field-${key}-label`}
              />
            </div>
          ))}
          <p className="text-xs text-callisto-muted">
            Switching a field off hides it from the form and stops new values
            being accepted into it. Values already recorded against it are kept —
            nothing is deleted — and reappear if it is switched back on.
          </p>
          <div className="flex justify-end">
            <Button
              onClick={save}
              disabled={saving}
              className="h-11 bg-callisto-navy hover:bg-callisto-navy/90 text-white"
              data-testid="settings-price-fields-save"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save size={14} className="mr-1.5" /> Save price fields</>)}
            </Button>
          </div>
        </div>
      )}
    </SectionShell>
  );
}

function HolidaysSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState("");
  const [name, setName] = useState("");
  const [adding, setAdding] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);

  function load() {
    setLoading(true);
    api.get("/settings/holidays")
      .then(({ data }) => setItems(data))
      .finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, []);

  async function add() {
    if (!date || !name.trim()) return toast.error("Pick a date and enter a name");
    setAdding(true);
    try {
      await api.post("/settings/holidays", { date, name: name.trim() });
      toast.success("Holiday added");
      setDate("");
      setName("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setAdding(false);
    }
  }

  async function remove() {
    if (!removeTarget) return;
    try {
      await api.delete(`/settings/holidays/${removeTarget.id}`);
      toast.success("Holiday removed");
      setRemoveTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  return (
    <SectionShell
      title="Holiday master"
      description="Company-recognised public holidays. Used by attendance and tour planning."
      icon={CalendarDays}
      testId="settings-holidays-section"
    >
      <div className="space-y-4">
        <div className="bg-callisto-app rounded-lg p-3 grid grid-cols-1 sm:grid-cols-[180px_1fr_auto] gap-2">
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="h-11 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
            data-testid="settings-holiday-date"
          />
          <Input
            placeholder="Holiday name e.g., Diwali"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="h-11 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
            data-testid="settings-holiday-name"
          />
          <Button
            onClick={add}
            disabled={adding}
            className="h-11 bg-callisto-navy hover:bg-callisto-navy/90 text-white"
            data-testid="settings-holiday-add"
          >
            {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Plus size={14} className="mr-1.5" /> Add</>)}
          </Button>
        </div>

        {loading ? (
          <Skeleton className="h-32 w-full" />
        ) : items.length === 0 ? (
          <p className="text-sm text-callisto-muted py-6 text-center">No holidays yet.</p>
        ) : (
          <ul className="divide-y divide-callisto border border-callisto rounded-lg overflow-hidden" data-testid="settings-holidays-list">
            {items.map((h) => (
              <li key={h.id} className="flex items-center justify-between gap-3 px-4 py-2.5 bg-white">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-callisto-navy truncate">{h.name}</p>
                  <p className="text-[11px] text-callisto-muted">{formatISTDayLabel(h.date)}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRemoveTarget(h)}
                  className="h-8 border-red-200 text-red-700 hover:bg-red-50 text-[11px]"
                  data-testid={`settings-holiday-remove-${h.id}`}
                >
                  <Trash2 size={12} className="mr-1" /> Remove
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <AlertDialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove holiday?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-semibold text-callisto-navy">{removeTarget?.name}</span> ({removeTarget?.date}) will no longer be treated as a holiday.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="settings-holiday-remove-confirm">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SectionShell>
  );
}

function StringListSection({ title, description, icon, endpoint, deleteParam, testIdPrefix }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [adding, setAdding] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);

  function load() {
    setLoading(true);
    api.get(endpoint)
      .then(({ data }) => setItems(data.items || []))
      .finally(() => setLoading(false));
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function add() {
    const trimmed = name.trim();
    if (!trimmed) return toast.error("Enter a name");
    setAdding(true);
    try {
      const { data } = await api.post(endpoint, { name: trimmed });
      setItems(data.items || []);
      setName("");
      toast.success("Added");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setAdding(false);
    }
  }

  async function remove() {
    if (!removeTarget) return;
    try {
      const { data } = await api.delete(`${endpoint}/${deleteParam(removeTarget)}`);
      setItems(data.items || []);
      toast.success("Removed");
      setRemoveTarget(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  return (
    <SectionShell title={title} description={description} icon={icon} testId={`settings-${testIdPrefix}-section`}>
      <div className="space-y-4">
        <div className="bg-callisto-app rounded-lg p-3 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
          <Input
            placeholder="Add new…"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()}
            className="h-11 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
            data-testid={`settings-${testIdPrefix}-input`}
          />
          <Button
            onClick={add}
            disabled={adding}
            className="h-11 bg-callisto-navy hover:bg-callisto-navy/90 text-white"
            data-testid={`settings-${testIdPrefix}-add`}
          >
            {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Plus size={14} className="mr-1.5" /> Add</>)}
          </Button>
        </div>
        {loading ? (
          <Skeleton className="h-24 w-full" />
        ) : (
          <div className="flex flex-wrap gap-2" data-testid={`settings-${testIdPrefix}-list`}>
            {items.map((it) => (
              <div
                key={it}
                className="inline-flex items-center gap-1.5 bg-white border border-callisto rounded-full pl-3 pr-1 py-1 text-xs font-semibold text-callisto-navy"
                data-testid={`settings-${testIdPrefix}-item-${it}`}
              >
                {it}
                <button
                  type="button"
                  onClick={() => setRemoveTarget(it)}
                  className="ml-1 h-6 w-6 rounded-full text-red-600 hover:bg-red-50 flex items-center justify-center"
                  data-testid={`settings-${testIdPrefix}-remove-${it}`}
                  aria-label={`Remove ${it}`}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove "{removeTarget}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Existing records that reference this value won't be deleted, but new entries won't see it.
              You can always add it back later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid={`settings-${testIdPrefix}-remove-confirm`}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SectionShell>
  );
}

// The three sections below are the missing EDITORS. All three values were
// already read by the app and none of them could be set from inside it:
// `cap_breaches` refused an over-cap claim against caps only a migration could
// write, `ucpmp.value_ceilings` told the Sales Officer "a Director adds it in
// Settings" where no Director could, and every doctor read path overlaid a
// per-category visit target nobody had a way to enter. A consumer with no
// editor runs forever on whatever the code shipped with — the same half-built
// shape `PriceFieldSection` had.
//
// NO LIST IS SPELLED HERE. The five expense categories and the three doctor
// categories both come from the endpoint that serves them, so a Director
// renaming one, or a migration renaming three (#78), moves the form with it.
// `backend/tests/test_master_list_guard.py` is what makes that hold.

// A blank box is a BLANK, and blank is not zero. `Number("")` is 0 in
// JavaScript, which is exactly how "no cap" would become "refuse everything in
// this category" — so the conversion is explicit and one-way: an empty string
// becomes `null`, and `null` is what the endpoints accept as "clear it".
function blankToNull(raw) {
  return String(raw ?? "").trim() === "" ? null : Number(raw);
}

// `0` and `null` are different answers and must not render the same. `?? ""`
// alone would be right and `|| ""` would be wrong — spelled out because the
// second is the reflex and it turns a real cap of 0 into an empty box.
function toInput(value) {
  return value === null || value === undefined ? "" : String(value);
}

const NOT_SET = "Not set";

function ExpenseCapsSection() {
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState("");
  const [vals, setVals] = useState({});
  const [saving, setSaving] = useState(false);

  function absorb(data) {
    setDoc(data);
    setVals(Object.fromEntries(
      (data.categories || []).map((c) => [c, toInput(data.caps?.[c])])));
  }

  useEffect(() => {
    api.get("/settings/expense-caps")
      .then(({ data }) => absorb(data))
      // A failed load says so rather than sitting on a skeleton. A section that
      // silently shows nothing reads as "there is nothing to configure", and
      // here that would read as "nothing is capped" — which is a statement
      // about company money that the screen has no basis to make.
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, []);

  async function save() {
    const caps = {};
    for (const category of doc.categories) {
      const value = blankToNull(vals[category]);
      if (value !== null && (!Number.isFinite(value) || value < 0 || value > MAX_THRESHOLD)) {
        return toast.error(
          `${category}: enter an amount between ₹0 and ${formatINR(MAX_THRESHOLD)}, or leave it blank for no cap`);
      }
      caps[category] = value;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/settings/expense-caps", { caps });
      absorb(data);
      toast.success("Expense caps saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <SectionShell
      title="Expense caps"
      description="The most a single claim may total in each category. A claim over its cap is refused when it is filed, when it is edited and again when it is approved — the message names the category, the cap and this setting. Leave a box blank for no cap."
      icon={IndianRupee}
      testId="settings-expense-caps-section"
    >
      {error ? (
        <div
          className="rounded-lg border border-red-200 bg-red-50 text-red-800 px-3 py-2.5 text-sm"
          data-testid="settings-expense-caps-error"
        >
          {error}
        </div>
      ) : !doc ? (
        <Skeleton className="h-32 w-full" />
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {doc.categories.map((category) => (
              <div key={category}>
                <Label className="text-callisto-navy">{category}</Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  max={MAX_THRESHOLD}
                  step="0.01"
                  value={vals[category] ?? ""}
                  placeholder={NOT_SET}
                  onChange={(e) => setVals((v) => ({ ...v, [category]: e.target.value }))}
                  className="h-11 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                  data-testid={`settings-expense-cap-${category}`}
                />
                <p className="mt-1 text-[11px] text-callisto-muted"
                   data-testid={`settings-expense-cap-current-${category}`}>
                  {doc.caps[category] === null || doc.caps[category] === undefined
                    ? `${NOT_SET} — no cap on this category`
                    : `Capped at ${formatINR(doc.caps[category])} per claim`}
                </p>
              </div>
            ))}
          </div>

          {doc.orphaned?.length > 0 && (
            // A cap whose category is no longer offered caps NOTHING. Shown as
            // the gap it is rather than hidden, because a stored value with no
            // row on the screen is one nobody can see or clear.
            <div
              className="rounded-lg border border-amber-200 bg-amber-50 text-amber-900 px-3 py-2.5 text-xs"
              data-testid="settings-expense-caps-orphaned"
            >
              {doc.orphaned.map((o) => (
                <p key={o.category}>
                  “{o.category}” is capped at {formatINR(o.cap)} but is no longer
                  an expense category, so the cap applies to nothing. Add the
                  category back or ask for the cap to be removed.
                </p>
              ))}
            </div>
          )}

          <p className="text-xs text-callisto-muted">
            A blank box is not a cap of ₹0. Blank means the category has no
            ceiling; ₹0 would refuse every claim in it.
          </p>

          <div className="flex justify-end">
            <Button
              onClick={save}
              disabled={saving}
              className="h-11 bg-callisto-navy hover:bg-callisto-navy/90 text-white"
              data-testid="settings-expense-caps-save"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save size={14} className="mr-1.5" /> Save caps</>)}
            </Button>
          </div>
        </div>
      )}
    </SectionShell>
  );
}

// UCPMP caps promotional spend at 2% of annual domestic sales, and this system
// has no sales data — no primary sales, no secondary sales, no invoice feed,
// and deliberately no pipeline to build one. So the figure is typed in, per
// financial year, and the ceiling computes from it.
//
// Until this card existed, `ucpmp.value_ceilings` told every Sales Officer on
// the Samples page that "a Director adds it in Settings" and no Director could.
// This is that sentence becoming true.
function UcpmpSalesBasisSection() {
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  function load(year) {
    return api.get("/settings/sales-basis", year ? { params: { financial_year: year } } : undefined)
      .then(({ data }) => { setDoc(data); return data; })
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }

  useEffect(() => { load(); }, []);

  async function save() {
    // `gt=0` on the backend, mirrored here: a basis of zero makes the 2%
    // ceiling zero, and a ceiling of zero reads as "well inside the limit"
    // rather than "not configured". A year with no sales is left unset.
    const value = Number(amount);
    if (String(amount).trim() === "" || !Number.isFinite(value) || value <= 0) {
      return toast.error("Enter the year's domestic sales figure in rupees");
    }
    setSaving(true);
    try {
      await api.post("/settings/sales-basis", {
        financial_year: doc.financial_year,
        amount_rupees: value,
        note: note.trim(),
      });
      setAmount("");
      setNote("");
      await load(doc.financial_year);
      toast.success("Sales basis recorded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <SectionShell
      title="UCPMP"
      description="Annual domestic sales for the financial year. UCPMP caps promotional spend at 2% of it, and this is the only place that figure comes from — nothing else in the system knows the company's sales."
      icon={ShieldCheck}
      testId="settings-ucpmp-section"
    >
      {error ? (
        <div
          className="rounded-lg border border-red-200 bg-red-50 text-red-800 px-3 py-2.5 text-sm"
          data-testid="settings-ucpmp-error"
        >
          {error}
        </div>
      ) : !doc ? (
        <Skeleton className="h-24 w-full" />
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-callisto-navy" data-testid="settings-ucpmp-current">
            <span className="font-semibold">{doc.financial_year}</span>
            {" — "}
            {doc.current
              ? `${formatINR(doc.current.amount_rupees)} (recorded by ${doc.current.recorded_by_name || "—"})`
              : `${NOT_SET}. The 2% promotional-spend ceiling cannot be worked out until it is entered.`}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-callisto-navy">
                Annual domestic sales for {doc.financial_year} (₹)
              </Label>
              <Input
                type="number"
                inputMode="decimal"
                min={0}
                step="0.01"
                value={amount}
                placeholder={NOT_SET}
                onChange={(e) => setAmount(e.target.value)}
                className="h-11 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                data-testid="settings-ucpmp-amount"
              />
            </div>
            <div>
              <Label className="text-callisto-navy">Note (optional)</Label>
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Audited figure, board pack March"
                className="h-11 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                data-testid="settings-ucpmp-note"
              />
            </div>
          </div>

          <p className="text-xs text-callisto-muted">
            Recording a figure does not overwrite the last one. A correction is a
            new entry for the same year and both stay readable, so “the basis was
            ₹4 crore when this ceiling was computed” can still be answered.
          </p>

          {doc.history?.length > 0 && (
            <ul
              className="divide-y divide-callisto border border-callisto rounded-lg overflow-hidden"
              data-testid="settings-ucpmp-history"
            >
              {doc.history.map((row) => (
                <li key={row.recorded_at} className="px-4 py-2 bg-white text-xs">
                  <span className="font-semibold text-callisto-navy">
                    {formatINR(row.amount_rupees)}
                  </span>
                  {" · "}
                  {row.recorded_by_name || "—"}
                  {row.note ? ` · ${row.note}` : ""}
                </li>
              ))}
            </ul>
          )}

          <div className="flex justify-end">
            <Button
              onClick={save}
              disabled={saving}
              className="h-11 bg-callisto-navy hover:bg-callisto-navy/90 text-white"
              data-testid="settings-ucpmp-save"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save size={14} className="mr-1.5" /> Save sales basis</>)}
            </Button>
          </div>
        </div>
      )}
    </SectionShell>
  );
}

// D12. The number a doctor of each category should be seen this many times a
// month. The READ path is untouched by this card and must stay that way: the
// setting wins on every doctor read, a doctor's own `monthly_visit_target` is
// used only where no setting exists, and the rep's original words are
// provenance nothing serves.
function VisitFrequencySection() {
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState("");
  const [vals, setVals] = useState({});
  const [saving, setSaving] = useState(false);

  function absorb(data) {
    setDoc(data);
    setVals(Object.fromEntries(
      (data.categories || []).map((c) => [c, toInput(data.targets?.[c])])));
  }

  useEffect(() => {
    api.get("/settings/visit-frequency")
      .then(({ data }) => absorb(data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, []);

  async function save() {
    const targets = {};
    for (const category of doc.categories) {
      const value = blankToNull(vals[category]);
      if (value !== null && (!Number.isInteger(value) || value < 0 || value > MAX_MONTHLY_VISITS)) {
        return toast.error(
          `Category ${category}: enter a whole number of visits between 0 and ${MAX_MONTHLY_VISITS}, or leave it blank for no company rule`);
      }
      targets[category] = value;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/settings/visit-frequency", { targets });
      absorb(data);
      toast.success("Visit frequency targets saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <SectionShell
      title="Visit frequency targets"
      description="How many times a month a doctor in each category should be seen. Saving one applies to every doctor in that category immediately — a doctor's own figure is only used where no target is set here."
      icon={Target}
      testId="settings-visit-frequency-section"
    >
      {error ? (
        <div
          className="rounded-lg border border-red-200 bg-red-50 text-red-800 px-3 py-2.5 text-sm"
          data-testid="settings-visit-frequency-error"
        >
          {error}
        </div>
      ) : !doc ? (
        <Skeleton className="h-24 w-full" />
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {doc.categories.map((category) => (
              <div key={category}>
                <Label className="text-callisto-navy">Category {category}</Label>
                <Input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={MAX_MONTHLY_VISITS}
                  step="1"
                  value={vals[category] ?? ""}
                  placeholder={NOT_SET}
                  onChange={(e) => setVals((v) => ({ ...v, [category]: e.target.value }))}
                  className="h-11 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                  data-testid={`settings-visit-frequency-${category}`}
                />
                <p className="mt-1 text-[11px] text-callisto-muted"
                   data-testid={`settings-visit-frequency-current-${category}`}>
                  {doc.targets[category] === null || doc.targets[category] === undefined
                    ? `${NOT_SET} — each doctor keeps their own figure`
                    : `${doc.targets[category]} visits a month`}
                </p>
              </div>
            ))}
          </div>
          <p className="text-xs text-callisto-muted">
            A blank box is not zero. Blank means there is no company rule for
            that category; 0 would mean those doctors are not to be visited.
          </p>
          <div className="flex justify-end">
            <Button
              onClick={save}
              disabled={saving}
              className="h-11 bg-callisto-navy hover:bg-callisto-navy/90 text-white"
              data-testid="settings-visit-frequency-save"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save size={14} className="mr-1.5" /> Save targets</>)}
            </Button>
          </div>
        </div>
      )}
    </SectionShell>
  );
}
